if (!customElements.get('product-engraving')) {
  class ProductEngraving extends HTMLElement {
    connectedCallback() {
      this.charLimit = parseInt(this.dataset.charLimit, 10);
      if (Number.isNaN(this.charLimit) || this.charLimit < 1) {
        this.charLimit = 10;
      }

      this.minChars = parseInt(this.dataset.minChars, 10) || 1;
      this.priceShort = this.dataset.priceShort || '';
      this.priceFormatted = this.dataset.priceFormatted || '';
      this.engravingVariantId = parseInt(this.dataset.engravingVariantId, 10) || 0;
      this.basePriceCents = 0;

      this.input = this.querySelector('.product-engraving__input');
      this.propertyInput = this.querySelector('.product-engraving__property');
      this.toggle = this.querySelector('.product-engraving__toggle');
      this.panel = this.querySelector('.product-engraving__field');
      this.counter = this.querySelector('.product-engraving__counter');
      this.error = this.querySelector('.product-engraving__error');
      this.counterTemplate = this.counter?.dataset.template || '';
      this.priceContainer = this.closest('.js-product')?.querySelector('.product-price .price__current > span:last-child, .product-price .price__current span:last-child');

      this.input?.addEventListener('input', () => this.handleInput());
      this.input?.addEventListener('keydown', (evt) => this.handleKeydown(evt));
      this.input?.addEventListener('paste', (evt) => this.handlePaste(evt));
      this.input?.addEventListener('blur', () => this.validate());
      this.toggle?.addEventListener('change', () => this.handleToggle());

      this.form = this.closest('form') || document.getElementById(this.propertyInput?.getAttribute('form') || '');
      if (this.form) {
        this.form.addEventListener('submit', (evt) => {
          if (!this.validate()) {
            evt.preventDefault();
            evt.stopImmediatePropagation();
            return;
          }
          this.syncPropertyValue(true);
        }, true);
      }

      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      document.addEventListener('on:variant:change', this.boundHandleVariantChange);

      this.setToggleState(false);
      this.refreshBasePriceCents();
      this.syncPropertyValue(false);
      this.updateProductPrice();
    }

    disconnectedCallback() {
      document.removeEventListener('on:variant:change', this.boundHandleVariantChange);
    }

    handleVariantChange(evt) {
      const section = this.closest('.shopify-section');
      const changedSection = evt.target?.closest?.('.shopify-section');
      if (!section || section !== changedSection) return;

      window.requestAnimationFrame(() => {
        this.refreshBasePriceCents();
        this.updateProductPrice();
      });
    }

    refreshBasePriceCents() {
      const priceEl = this.closest('.js-product')?.querySelector('.product-price .price__current > span:last-child, .product-price .price__current span:last-child');
      this.priceContainer = priceEl || this.priceContainer;

      if (!this.priceContainer) return;

      const priceText = this.priceContainer.textContent || '';
      this.basePriceText = priceText;
      const normalized = priceText
        .replace(/[^\d,.-]/g, '')
        .replace(/\./g, '')
        .replace(',', '.');

      const parsed = parseFloat(normalized);
      if (!Number.isNaN(parsed)) {
        this.basePriceCents = Math.round(parsed * 100);
      }
    }

    formatMoney(cents) {
      const format = theme.settings.moneyFormat || theme.settings.moneyWithCurrencyFormat || '{{amount}}';
      const amount = (cents / 100).toFixed(2);
      const amountWithComma = amount.replace('.', ',');
      const amountParts = amount.split('.');
      const integerWithSeparator = (separator) => amountParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator);
      const amountWithCommaSeparator = integerWithSeparator('.') + (amountParts[1] ? `,${amountParts[1]}` : '');
      const amountWithSpaceSeparator = integerWithSeparator(' ') + (amountParts[1] ? `,${amountParts[1]}` : '');
      const amountWithPeriodAndSpaceSeparator = integerWithSeparator(' ') + (amountParts[1] ? `.${amountParts[1]}` : '');
      const amountWithApostropheSeparator = integerWithSeparator("'") + (amountParts[1] ? `.${amountParts[1]}` : '');

      return format
        .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, integerWithSeparator(' '))
        .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, amountWithSpaceSeparator)
        .replace(/\{\{\s*amount_with_period_and_space_separator\s*\}\}/g, amountWithPeriodAndSpaceSeparator)
        .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amountWithApostropheSeparator)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, integerWithSeparator('.'))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithCommaSeparator)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountParts[0])
        .replace(/\{\{\s*amount\s*\}\}/g, amountWithComma);
    }

    getEngravingPriceCents() {
      if (!this.engravingVariantId) return 0;

      const priceCents = parseInt(this.dataset.engravingPriceCents, 10);
      if (!Number.isNaN(priceCents) && priceCents > 0) {
        return priceCents;
      }

      if (this.priceFormatted) {
        const normalized = this.priceFormatted
          .replace(/[^\d,.-]/g, '')
          .replace(/\./g, '')
          .replace(',', '.');
        const parsed = parseFloat(normalized);
        if (!Number.isNaN(parsed)) {
          return Math.round(parsed * 100);
        }
      }

      return 0;
    }

    updateProductPrice() {
      if (!this.priceContainer || !this.basePriceCents) return;

      const extraCents = this.hasEngravingText() ? this.getEngravingPriceCents() : 0;

      // Without an engraving surcharge, keep the server-rendered price
      // untouched so any currency/locale format stays exactly as Shopify
      // rendered it.
      if (extraCents === 0) {
        if (typeof this.basePriceText === 'string' && this.basePriceText !== '') {
          this.priceContainer.textContent = this.basePriceText;
        }
        return;
      }

      this.priceContainer.textContent = this.formatMoney(this.basePriceCents + extraCents);
    }

    normalizeText(value) {
      let text = (value || '').replace(/\s/g, '');

      if (text.length > this.charLimit) {
        text = text.slice(0, this.charLimit);
      }

      return text;
    }

    getCharCount(value) {
      return this.normalizeText(value).length;
    }

    hasEngravingText() {
      return this.isEnabled() && this.normalizeText(this.input?.value || '') !== '';
    }

    isEnabled() {
      return Boolean(this.toggle?.checked);
    }

    handleToggle() {
      this.setToggleState(this.isEnabled());
    }

    setToggleState(enabled) {
      if (this.panel) {
        this.panel.hidden = !enabled;
      }

      if (this.toggle) {
        this.toggle.setAttribute('aria-expanded', enabled ? 'true' : 'false');
      }

      if (this.input) {
        this.input.disabled = !enabled;
      }

      if (!enabled) {
        if (this.input) {
          this.input.value = '';
        }
        this.updateCounter();
        this.setError(false);
        this.syncPropertyValue(false);
      }

      this.updateProductPrice();
    }

    shouldAddEngravingProduct() {
      return this.hasEngravingText() && this.engravingVariantId > 0 && this.charLimit > 0;
    }

    static collectFormProperties(form) {
      const properties = {};

      document.querySelectorAll('[name^="properties["]').forEach((el) => {
        const formAttr = el.getAttribute('form');
        if (formAttr) {
          if (formAttr !== form.id) return;
        } else if (!form.contains(el)) {
          return;
        }

        if (el.disabled || !el.name) return;

        const match = el.name.match(/^properties\[(.+)\]$/);
        if (match && match[1]) {
          properties[match[1]] = el.value;
        }
      });

      return properties;
    }

    getLineItemProperties(form) {
      const properties = ProductEngraving.collectFormProperties(form);
      const text = this.normalizeText(this.input?.value || '');

      if (this.propertyInput?.name) {
        const match = this.propertyInput.name.match(/^properties\[(.+)\]$/);
        if (match?.[1]) {
          delete properties[match[1]];
        }
      }

      if (!text) return properties;

      if (this.propertyInput?.name) {
        const match = this.propertyInput.name.match(/^properties\[(.+)\]$/);
        if (match?.[1]) {
          properties[match[1]] = this.formatPropertyValue(text);
        }
      }

      return properties;
    }

    appendPropertiesToFormData(formData, form) {
      if (!this.hasEngravingText()) {
        if (this.propertyInput?.name) {
          formData.delete(this.propertyInput.name);
        }
        return;
      }

      this.syncPropertyValue(true);

      const properties = this.getLineItemProperties(form);
      Object.entries(properties).forEach(([key, value]) => {
        if (value !== '') {
          formData.set(`properties[${key}]`, value);
        }
      });
    }

    buildEngravingCartPayload(form, sections) {
      if (!this.shouldAddEngravingProduct()) return null;

      const parentVariantId = parseInt(form.querySelector('[name="id"]')?.value, 10);
      if (!parentVariantId) return null;

      const quantity = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);
      const properties = this.getLineItemProperties(form);
      const childItem = {
        id: this.engravingVariantId,
        quantity,
        parent_id: parentVariantId
      };

      const engravingImage = this.dataset.engravingImage;
      if (engravingImage) {
        childItem.properties = { _thumbnail: engravingImage };
      }

      const items = [
        {
          id: parentVariantId,
          quantity,
          properties
        },
        childItem
      ];

      const payload = { items };

      if (sections?.length) {
        payload.sections = sections.join(',');
        payload.sections_url = window.location.pathname;
      }

      return payload;
    }

    handleKeydown(evt) {
      if (!this.input) return;

      if (evt.ctrlKey || evt.metaKey || evt.altKey) return;

      const allowedKeys = [
        'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Tab', 'Home', 'End'
      ];
      if (allowedKeys.includes(evt.key)) return;

      if (evt.key.length !== 1) return;

      const value = this.input.value;
      const start = this.input.selectionStart ?? value.length;
      const end = this.input.selectionEnd ?? value.length;
      const nextValue = value.slice(0, start) + evt.key + value.slice(end);

      if (this.getCharCount(nextValue) > this.charLimit) {
        evt.preventDefault();
      }
    }

    handlePaste(evt) {
      if (!this.input) return;

      evt.preventDefault();
      const pasted = evt.clipboardData?.getData('text') || '';
      const value = this.input.value;
      const start = this.input.selectionStart ?? value.length;
      const end = this.input.selectionEnd ?? value.length;
      this.input.value = this.normalizeText(value.slice(0, start) + pasted + value.slice(end));
      this.handleInput();
    }

    updateCounter() {
      if (!this.counter) return;

      const current = this.getCharCount(this.input?.value || '');
      if (this.counterTemplate) {
        this.counter.textContent = this.counterTemplate.replace('[current]', current);
      } else {
        this.counter.textContent = `${current}/${this.charLimit}`;
      }
    }

    handleInput() {
      if (!this.input) return;
      this.input.value = this.normalizeText(this.input.value);
      this.updateCounter();
      this.setError(false);
      this.syncPropertyValue(false);
      this.updateProductPrice();
    }

    syncPropertyValue(forceEnable) {
      if (!this.isEnabled()) {
        if (this.propertyInput) {
          this.propertyInput.value = '';
          this.propertyInput.setAttribute('disabled', '');
        }
        return;
      }

      const text = this.normalizeText(this.input?.value || '');

      if (!this.propertyInput) return;

      if (text === '') {
        this.propertyInput.value = '';
        this.propertyInput.setAttribute('disabled', '');
        return;
      }

      this.propertyInput.value = this.formatPropertyValue(text);
      this.propertyInput.removeAttribute('disabled');
    }

    formatPropertyValue(text) {
      if (!text) return '';

      const childPriceShort = this.dataset.priceShort || this.priceShort;
      if (childPriceShort) {
        return `${text} (+${childPriceShort})`;
      }

      return text;
    }

    setError(show) {
      if (!this.error || !this.input) return;
      this.error.hidden = !show;
      this.input.classList.toggle('product-engraving__input--error', show);
      if (show) {
        this.input.setAttribute('aria-invalid', 'true');
      } else {
        this.input.removeAttribute('aria-invalid');
      }
    }

    validate() {
      if (!this.isEnabled()) {
        this.setError(false);
        return true;
      }

      const text = this.normalizeText(this.input?.value || '');

      if (text === '') {
        this.setError(false);
        return true;
      }

      const count = text.length;
      const isValid = count >= this.minChars && count <= this.charLimit;
      this.setError(!isValid);
      return isValid;
    }
  }

  customElements.define('product-engraving', ProductEngraving);
}